import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { servicesData } from '../data/services';
import { CheckCircle2, ArrowRight, MessageCircle, Phone, ShieldCheck, Zap, HeartHandshake, Rocket } from 'lucide-react';

const ServiceDetail = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  
  const service = servicesData[id];

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const translatedItems = t(`services.categories.${id}.items`, { returnObjects: true });

  const itBenefits = [
    { title: 'Experienced Team', icon: <ShieldCheck size={24} /> },
    { title: 'Affordable Pricing', icon: <Zap size={24} /> },
    { title: 'Custom Solutions', icon: <HeartHandshake size={24} /> },
    { title: 'Fast Project Delivery', icon: <Rocket size={24} /> }
  ];

  return (
    <div className="pt-10">
      {/* Header Section with Image Background */}
      <section className="relative py-32 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={service.image} alt="Background" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-dark-green/90 backdrop-blur-sm"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex items-center gap-4 mb-8">
            <Link to="/services" className="text-gold-light hover:text-white transition-colors flex items-center gap-2 font-medium">
              {t('nav.services')}
            </Link>
            <span className="text-gray-500">/</span>
            <span className="text-white font-bold">{t(`services.categories.${id}.title`)}</span>
          </div>
          
          <div className="flex flex-col md:flex-row items-center gap-10">
            <div className="p-6 bg-gold-primary rounded-[2rem] shadow-2xl transform -rotate-3 hover:rotate-0 transition-transform duration-500">
              {React.cloneElement(service.icon, { size: 64, className: "text-white" })}
            </div>
            <div className="text-center md:text-left">
              <h1 className="text-4xl md:text-6xl font-extrabold mb-6 tracking-tight">{t(`services.categories.${id}.title`)}</h1>
              <p className="text-xl text-gold-light max-w-2xl leading-relaxed italic">
                "{t(`services.categories.${id}.description`)}"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-16">
            {/* Left Content */}
            <div className="lg:col-span-2 space-y-16">
              <div>
                <h2 className="text-3xl font-extrabold text-dark-green mb-10 flex items-center gap-4">
                  <span className="w-12 h-1 bg-gold-primary rounded-full"></span>
                  Detailed Offerings
                </h2>
                <div className="grid md:grid-cols-2 gap-8">
                  {Object.entries(translatedItems).map(([key, item], index) => (
                    <div key={index} className="bg-cream-off p-8 rounded-3xl border border-gold-light/20 hover:border-gold-primary hover:bg-white hover:shadow-xl transition-all duration-300 group">
                      <div className="w-14 h-14 bg-dark-green rounded-2xl flex items-center justify-center mb-6 text-gold-primary group-hover:bg-gold-primary group-hover:text-white transition-all shadow-md">
                        {service.items[index]?.icon || <CheckCircle2 size={28} />}
                      </div>
                      <h3 className="text-xl font-bold text-dark-green mb-3">{item.name}</h3>
                      <p className="text-gray-600 leading-relaxed text-sm">
                        {item.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Special IT Benefits Section */}
              {id === 'itDigitalServices' && (
                <div className="bg-dark-green-deep p-12 rounded-[3rem] text-white shadow-2xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-gold-primary/10 rounded-full blur-3xl -mr-32 -mt-32"></div>
                  <h3 className="text-3xl font-bold mb-10 text-center text-gold-primary">Why Choose VSK Associates for IT?</h3>
                  <div className="grid md:grid-cols-2 gap-8">
                    {itBenefits.map((benefit, i) => (
                      <div key={i} className="flex items-center gap-6 bg-white/5 p-6 rounded-2xl border border-white/10 hover:bg-white/10 transition-colors">
                        <div className="text-gold-primary">{benefit.icon}</div>
                        <span className="font-bold text-lg">{benefit.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Success Banner */}
              <div className="p-12 bg-gold-primary rounded-[3rem] text-dark-green-deep flex flex-col md:flex-row items-center justify-between gap-8 shadow-2xl">
                <div className="text-center md:text-left">
                  <h3 className="text-3xl font-black mb-2">Ready to Get Started?</h3>
                  <p className="font-bold opacity-80">Book a free consultation with our experts today.</p>
                </div>
                <Link to="/enquiry" className="bg-dark-green text-white px-10 py-5 rounded-2xl font-black uppercase tracking-widest hover:bg-dark-green-deep transition-all shadow-xl active:scale-95 whitespace-nowrap">
                  Enquire Now
                </Link>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-10">
              {/* Service Navigation */}
              <div className="bg-cream-off p-8 rounded-[2rem] border border-gold-light/20 shadow-sm">
                <h4 className="text-xl font-black text-dark-green mb-8 uppercase tracking-tighter border-b border-gold-primary/30 pb-4">Other Services</h4>
                <div className="space-y-4">
                  {Object.keys(servicesData).map((key) => (
                    <Link 
                      key={key}
                      to={`/services/${key}`}
                      className={`flex items-center justify-between p-5 rounded-2xl transition-all font-bold group ${
                        id === key 
                          ? 'bg-dark-green text-white shadow-xl translate-x-2' 
                          : 'bg-white text-dark-green hover:bg-gold-primary/10 hover:translate-x-2'
                      }`}
                    >
                      <span>{t(`services.categories.${key}.title`)}</span>
                      <ArrowRight size={18} className={id === key ? "text-gold-primary" : "text-gray-300 group-hover:text-gold-primary"} />
                    </Link>
                  ))}
                </div>
              </div>

              {/* Direct Contact Card */}
              <div className="relative rounded-[2rem] overflow-hidden group shadow-2xl">
                <img src="/assets/images/ChatGPT Image Jun 5, 2026, 05_33_14 PM.png" alt="Contact" className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-dark-green/80 flex flex-col justify-center p-8 text-white">
                  <h4 className="text-2xl font-black mb-4 leading-tight">Need Immediate Assistance?</h4>
                  <p className="text-gold-light mb-8 font-medium">Our consultants are available 10 AM - 7 PM.</p>
                  <div className="space-y-4">
                    <a href="tel:8936835037" className="flex items-center gap-4 p-4 bg-white text-dark-green rounded-2xl font-black hover:bg-gold-primary hover:text-white transition-all">
                      <Phone size={24} />
                      <span>8936835037</span>
                    </a>
                    <a href="https://wa.me/918936835037" className="flex items-center gap-4 p-4 bg-green-whatsapp text-white rounded-2xl font-black hover:opacity-90 transition-all shadow-lg">
                      <MessageCircle size={24} />
                      <span>WhatsApp Chat</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
